import React, { useState, useRef, useEffect } from 'react';
import { Container, Section } from 'components/shared';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import { RichText } from '@sitecore-jss/sitecore-jss-react';
import styled from 'styled-components';
import cx from 'classnames';
import { getFieldValue } from '@sitecore-jss/sitecore-jss-react';
import { isValidTitle } from 'utils/validateTitle';

import { Button } from 'components/components/atoms';
import { PortraitCard } from 'components/components/molecules';

// Flex container for centering or left-aligning based on condition
const FlexContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

// CarouselContainer with flex styles for centering
const CarouselContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;

  & ul,
  & .BrainhubCarousel,
  & .BrainhubCarousel__trackContainer {
    overflow: visible !important;
  }
`;

const Heading = styled.h2`
  max-width: 32.063rem;
  letter-spacing: -0.5px;
  line-height: 2.625rem;
`;

const Copy = styled.p`
  max-width: 39.938rem;
  letter-spacing: 0.11px;
`;

const CarouselWrapper = ({ children, isMobile }) =>
  isMobile ? children : <Container>{children}</Container>;

const PortraitCards = ({ fields }) => {
  const portraitCards = (getFieldValue(fields, 'portraitCards') || [])[0] || {};
  const breakpoint = useBreakpoint(
    typeof window !== 'undefined' && window.innerWidth,
  );
  const isMobile = ['sm', 'xs'].includes(breakpoint);
  const isLandscape = ['md', 'lg'].includes(breakpoint);

  const [bigSize, setBigSize] = useState(false);
  const [detectFontSize, setDetectFontSize] = useState(false);
  const MediaSize = isMobile ? true : bigSize ? true : false;

  const itemRefShowMore = useRef([]);
  const [lastDesktopVisibleCards, setLastDesktopVisibleCards] = useState([]);
  // Derived on every render, never captured in a useState initializer:
  // useBreakpoint() returns SSR_BREAKPOINT ('xs') on the first pass and the
  // real value only after mount, so an initializer would freeze the desktop
  // grid at the mobile page size permanently.
  const desktopPageSize = isMobile ? 1 : isLandscape ? 4 : 6;
  const desktopPageStep = isMobile ? 1 : isLandscape ? 2 : 3;
  const [desktopExtraPages, setDesktopExtraPages] = useState(0);
  const desktopVisibleCards =
    desktopPageSize + desktopExtraPages * desktopPageStep;
  const [dynamicClass, setDynamicClass] = useState(
    'md:grid-cols-2 xl:grid-cols-3 ',
  );
  const itemRefs = useRef([]);
  const targetRefFontSize = useRef();

  useEffect(() => {
    const observer = new ResizeObserver(checkFontSizeChange);
    if (targetRefFontSize && targetRefFontSize.current) {
      observer.observe(targetRefFontSize.current);
    }
    return () => {
      observer.disconnect();
    };
  }, []);

  const checkFontSizeChange = () => {
    if (targetRefFontSize.current) {
      const fontSize = parseInt(
        getComputedStyle(targetRefFontSize.current).fontSize,
        10,
      );
      if (fontSize > 20) {
        setDynamicClass(' ');
      } else {
        setDynamicClass(' md:grid-cols-2 xl:grid-cols-3 ');
      }
    }
  };

  const getPortraitSlides = (cards) => {
    return cards.map((c, i) => (
      <div
        key={c.thumbnail}
        className={
          'p-2 md:pb-16 md:pt-4' + (i < desktopVisibleCards ? '' : ' hidden')
        }
        aria-hidden={!MediaSize && i < 2 ? 'false' : i === 0 ? 'false' : 'true'}
        ref={(el) => (itemRefs.current[i] = el)}
      >
        <PortraitCard
          indexAria={true}
          card={c}
          data-aos={!detectFontSize ? 'fade-left' : null}
          data-aos-delay={!detectFontSize ? i * 150 : false}
        />
      </div>
    ));
  };

  const slides = getPortraitSlides(portraitCards.portraitCards || []);

  useEffect(() => {
    const myElement = itemRefs.current[0];
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        entry.contentRect.width > 452 ? setBigSize(true) : setBigSize(false);
        setDetectFontSize(true);
      }
    });
    if (myElement) {
      observer.observe(myElement);
    }
    return () => {
      observer.disconnect();
    };
  }, []);

  if (portraitCards.portraitCards.length < 1) return null;

  return (
    <Section
      className={cx({
        'bg-light-blue': portraitCards.blueBackground,
        'bg-white': !portraitCards.blueBackground,
      })}
    >
      {/* Flex Container to center-align or left-align based on checkbox */}
      <FlexContainer
        className={cx('py-8', {
          'items-start text-left': portraitCards.titleLeftAlign, // Apply left-alignment to the entire section
          'items-center text-center': !portraitCards.titleLeftAlign, // Apply center-alignment to the entire section
        })}
      >
        {isValidTitle(portraitCards.heading) && (
          <Heading
            className={cx(
              'font-bold font-trade-gothic-20 mb-6 md:mb-2 text-4xl text-mattamy-blue',
              {
                'text-left': portraitCards.titleLeftAlign, // Left align if TextLeftAlign is true
                'text-center': !portraitCards.titleLeftAlign, // Center align if TextLeftAlign is false
              },
            )}
            data-aos='fade-down'
          >
            <RichText
              tag='span'
              field={{ value: portraitCards.heading }}
              className='richtext'
            />
          </Heading>
        )}

        <Copy
          className={cx(
            'text-normal mb-8 md:mb-2 font-graphie text-mattamy-blue',
            {
              'text-left': portraitCards.titleLeftAlign, // Left align if TextLeftAlign is true
              'text-center': !portraitCards.titleLeftAlign, // Center align if TextLeftAlign is false
            },
          )}
          data-aos='fade-down'
          data-aos-delay='100'
          data-aos-offset='100'
        >
          <RichText
            tag='span'
            field={{ value: portraitCards.text }}
            className='richtext'
          />
        </Copy>
      </FlexContainer>

      <CarouselWrapper bigSize={bigSize} MediaSize={MediaSize}>
        <CarouselContainer
          aria-labelledby='this is slider area'
          className='relative w-full md:pt-8'
        >
          <div
            className={cx('relative w-full h-full flex flex-col space-between')}
          >
            <div
              ref={targetRefFontSize}
              className={`w-full md:mt-0 grid grid-cols-1 ${dynamicClass} gap-4`}
            >
              {/* Render portrait card slides */}
              {slides}
            </div>
            <div className='w-full mt-0 text-center'>
              <Button
                className={cx(
                  'mt-4 focus:bg-mattamy-blue-dark focus:text-white',
                  {
                    hidden:
                      portraitCards.portraitCards.length <= desktopVisibleCards,
                  },
                )}
                color={'mattamy-blue'}
                variant='filled'
                buttonDescription={'SHOW MORE'}
                onClick={() => {
                  if (
                    portraitCards.portraitCards.length > desktopVisibleCards
                  ) {
                    setDesktopExtraPages((n) => n + 1);
                  }

                  setLastDesktopVisibleCards((prvs) => [
                    ...prvs,
                    desktopVisibleCards,
                  ]);
                }}
              >
                <p className='w-40'>{'SHOW MORE'}</p>
              </Button>
            </div>
          </div>
        </CarouselContainer>
      </CarouselWrapper>
    </Section>
  );
};

export default PortraitCards;
